/**
 * stream-md/shiki — Shiki-backed syntax highlighter (lazy).
 *
 * Shiki uses TextMate grammars + VS Code themes for production-grade syntax
 * highlighting. Required as an *optional* peer dependency (zero cost if
 * not imported).
 *
 * @example
 * ```tsx
 * import { StreamMD } from 'stream-md';
 * import { createShikiHighlighter } from 'stream-md/shiki';
 *
 * const highlighter = await createShikiHighlighter({
 *   theme: 'github-dark',
 *   langs: ['ts', 'tsx', 'python', 'rust'],
 * });
 *
 * <StreamMD text={text} highlighter={highlighter} />
 * ```
 */

import type { HighlighterFn } from "../parser/types";

export interface ShikiHighlighterOptions {
  /** Theme name or theme object. */
  theme?: string;
  /** Languages to load up-front. Others are lazy-loaded on first use. */
  langs?: string[];
}

interface ShikiTokenLike {
  content: string;
  color?: string;
}

interface ShikiHighlighterLike {
  codeToTokens: (
    code: string,
    options: { lang: string; theme: string },
  ) => { tokens: ShikiTokenLike[][] };
  loadLanguage: (lang: string) => Promise<void>;
  getLoadedLanguages: () => string[];
}

/**
 * Create a Shiki-backed highlighter. Returns an async function compatible
 * with `<StreamMD highlighter={...} />`.
 *
 * Note: highlighter is async at create time, but synchronous when called
 * from the component (uses cached results).
 */
export async function createShikiHighlighter(
  options: ShikiHighlighterOptions = {},
): Promise<HighlighterFn> {
  const theme = options.theme ?? "github-dark";
  const langs = options.langs ?? ["javascript", "typescript", "tsx", "python", "rust", "go", "bash", "json"];

  const shiki = (await import("shiki")) as unknown as {
    createHighlighter?: (opts: { themes: string[]; langs: string[] }) => Promise<ShikiHighlighterLike>;
    getHighlighter?: (opts: { themes: string[]; langs: string[] }) => Promise<ShikiHighlighterLike>;
  };
  // shiki@1+ uses createHighlighter; older uses getHighlighter.
  const factory = shiki.createHighlighter ?? shiki.getHighlighter;
  if (!factory) throw new Error("[stream-md/shiki] No createHighlighter/getHighlighter export found");
  const highlighter = await factory({
    themes: [theme],
    langs,
  });

  const cache = new Map<string, Array<{ text: string; className: string }>>();

  return (code: string, language: string) => {
    const key = `${language} ${code}`;
    const cached = cache.get(key);
    if (cached) return cached;

    const lang = language || "text";
    if (!highlighter.getLoadedLanguages().includes(lang)) {
      // Async-load and return plain for now; subsequent calls will hit the cache.
      void highlighter.loadLanguage(lang).catch(() => undefined);
      return [{ text: code, className: "" }];
    }

    let result;
    try {
      result = highlighter.codeToTokens(code, { lang, theme });
    } catch {
      return [{ text: code, className: "" }];
    }
    const tokens: Array<{ text: string; className: string }> = [];
    result.tokens.forEach((line, li) => {
      for (const t of line) {
        // Use inline style via a className that consumers can target by color.
        // To keep CSP-safe (no inline styles), we encode the color in a custom
        // CSS variable through a className. Consumers needing exact colors
        // should use Shiki's HTML output directly via a `pre` override.
        const cls = t.color ? `smd-shiki ${colorToClass(t.color)}` : "";
        tokens.push({ text: t.content, className: cls });
      }
      if (li < result.tokens.length - 1) tokens.push({ text: "\n", className: "" });
    });
    cache.set(key, tokens);
    return tokens;
  };
}

function colorToClass(color: string): string {
  return "smd-c-" + color.replace(/[^a-zA-Z0-9]/g, "");
}
